import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { DahliaMark, HeritageSeal } from '@/components/BrandMark';
import {
  LayoutDashboard, Package, Palette, Sparkles, Truck, Newspaper, Vault,
  Lightbulb, Award, User, LogOut, Menu, BookOpen, Receipt, Lock, ShoppingBag, ScrollText,
} from 'lucide-react';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import FounderInvoicePage from '@/pages/founder/FounderInvoicePage';

const LINKS = [
  { to: '/console/founder', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/console/founder/bundle', label: 'My Founder Bundle', icon: Package },
  { to: '/console/founder/catalog', label: 'Catalogue', icon: BookOpen },
  { to: '/console/founder/store', label: 'Founder Store', icon: ShoppingBag },
  { to: '/console/founder/orders', label: 'My Orders', icon: ScrollText },
  { to: '/console/founder/design', label: 'Design My Collection', icon: Palette },
  { to: '/console/founder/colourway', label: 'Founder Colourway', icon: Sparkles },
  { to: '/console/founder/production', label: 'Track My Tracksuit', icon: Truck },
  { to: '/console/founder/updates', label: 'Founder Updates', icon: Newspaper },
  { to: '/console/founder/vault', label: 'Founder Vault', icon: Vault },
  { to: '/console/founder/pitches', label: 'Pitch an Apparel Idea', icon: Lightbulb },
  { to: '/console/founder/rewards', label: 'Founder Rewards', icon: Award },
  { to: '/console/founder/invoice', label: 'My Invoice', icon: Receipt },
  { to: '/console/founder/account', label: 'My Account', icon: User },
];

export default function FounderConsoleLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checked, setChecked] = useState(false);

  const doLogout = () => { logout(); navigate('/'); };

  // Load payment gating status once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/founders/payment-status');
        setPaymentStatus(data);
      } catch (e) { console.error(e); }
      finally { setChecked(true); }
    })();
  }, [location.pathname]); // re-check whenever route changes so "Refresh" reflects verification

  const isPaid = paymentStatus?.payment_verified || paymentStatus?.status === 'paid';
  const isInvoiceRoute = location.pathname.endsWith('/invoice');

  return (
    <div className="min-h-screen" style={{ background: 'var(--heritage-cream)' }}>
      <ImpersonationBanner />
      <header className="border-b" style={{ borderColor: 'rgba(75,24,56,0.2)', background: 'rgba(246,240,228,0.95)' }}>
        <div className="max-w-[1500px] mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <DahliaMark size={44} />
            <div className="leading-none">
              <div className="h-serif text-lg tracking-[0.28em]" style={{ color: 'var(--heritage-plum)' }}>HERITAGE</div>
              <div className="h-eyebrow mt-1">Founders Console</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-sm" style={{ color: 'var(--heritage-plum)' }}>{user?.name}</div>
            <button onClick={doLogout} className="h-btn-outline text-xs" data-testid="founder-logout"><LogOut size={14} className="mr-2" />Logout</button>
            <button className="lg:hidden" onClick={() => setOpen(!open)}><Menu size={22} style={{ color: 'var(--heritage-plum)' }} /></button>
          </div>
        </div>
      </header>

      {/* Awaiting payment gating: full-screen invoice-only view */}
      {checked && !isPaid && (
        <div className="max-w-[1500px] mx-auto px-4 md:px-6 py-8">
          <div className="max-w-3xl mx-auto mb-4 flex items-center justify-between gap-3 no-print">
            <div>
              <div className="h-eyebrow flex items-center gap-2"><Lock size={12} /> Console access pending</div>
              <div className="h-display text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Awaiting payment</div>
              <div className="text-sm opacity-85 mt-1 max-w-xl" style={{ color: 'var(--heritage-plum)' }}>
                Your seat is reserved. Once we verify your bank deposit, your full Founders Console will unlock automatically.
              </div>
            </div>
          </div>
          <FounderInvoicePage />
        </div>
      )}

      {/* Paid: full console */}
      {checked && isPaid && (
        <div className="max-w-[1500px] mx-auto px-5 py-6 grid lg:grid-cols-[260px,1fr] gap-6">
          <aside className={`${open ? 'block' : 'hidden'} lg:block`}>
            <div className="h-panel-thin p-3 sticky top-4">
              <div className="px-3 pt-3 pb-2 text-center">
                <HeritageSeal size={60} />
              </div>
              <nav className="mt-2">
                {LINKS.map((l) => {
                  const Icon = l.icon;
                  return (
                    <NavLink key={l.to} to={l.to} end={l.end}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg my-0.5 text-sm transition-colors ${isActive ? 'font-medium' : 'opacity-80 hover:opacity-100'}`
                      }
                      style={({ isActive }) => ({
                        background: isActive ? 'rgba(75,24,56,0.09)' : 'transparent',
                        color: 'var(--heritage-plum)',
                      })}
                      data-testid={`founder-nav-${l.to.split('/').pop()}`}
                      onClick={() => setOpen(false)}
                    >
                      <Icon size={16} /> {l.label}
                    </NavLink>
                  );
                })}
                <button onClick={doLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg my-0.5 text-sm w-full opacity-80 hover:opacity-100" style={{ color: 'var(--heritage-plum)' }}>
                  <LogOut size={16} /> Logout
                </button>
              </nav>
            </div>
          </aside>
          <main>
            {isInvoiceRoute ? <FounderInvoicePage /> : <Outlet />}
          </main>
        </div>
      )}

      {!checked && (
        <div className="min-h-[60vh] flex items-center justify-center" style={{ color: 'var(--heritage-plum)' }}>
          <div className="h-eyebrow">Loading console...</div>
        </div>
      )}
    </div>
  );
}
